import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
import { useCallback, useEffect, useState } from "react";
import { RequestFormContext } from "../RequestFormContext";
import { getCenterStyles, getYCenterStyles, CardDocumentIcon } from "vig-ui-library";
import { CreateGenericChunkedUploader, GenerateUploadInput } from "../Utilities/GenericChunkedUploader";
import { ExtendedFile } from "../Utilities/UploadHelpers";
import { addIconsToFiles } from "../Utilities/mappers";
import { getRequestWithFiles } from "../Utilities/mappers";
import { utilities } from "../Utilities/file";
import { FileTile } from "./FileTile";
import { signatureFlowBodyBottomGapSx, signatureFlowContentGutterSx } from "../Utilities/signatureFlowLayout";
import { SignatureMethod } from "../Enums/SignatureMethod";

export type UploadIdCardViewProps = Omit<RequestFormContext, "pathToProp">;

export const UploadIdCardView = (props: UploadIdCardViewProps) => {
    const theme = useTheme();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const { request, openSnackbar, getIdCardFrontFileId, getIdCardBackFileId, idCardFrontTaxonomyId, idCardBackTaxonomyId, rerenderForm } = props;

    // Create upload input for ID card upload endpoint
    const generateUploadInput = useCallback((
        file: Record<string, any>,
        byteArray: Uint8Array,
        isEndOfFile: boolean
    ) => {
        return {
            userName: undefined, // Will be set by backend
            requestId: request.current.requestId, // Use request ID from context instead of file.requestId
            isUploadFinished: isEndOfFile,
            uploadSessionId: file.sessionId,
            fileToUpload: {
                id: file.id,
                fileId: file.id,
                fileName: file.name,
                fileExtension: file.extension,
                fileChunk: byteArray,
                size: file.size,
                taxonomyId: undefined, // Backend will detect via AI
            },
        };
    }, [request]);

    const uploadErrorHandler = useCallback((file: Record<string, any>) => {
        let index = -1;
        const currentRequestWithFiles = getRequestWithFiles(props.request);
        for (let i = 0; i < currentRequestWithFiles.current.files.length; i++) {
            if (file.id === currentRequestWithFiles.current.files[i].id) {
                index = i;
                break;
            }
        }
        if (index >= 0) {
            currentRequestWithFiles.current.files.splice(index, 1);
        }
        props.uploadInCompletedNotifierTask.debouncedPerform();
        setIsUploading(false);
        // Only set error if we don't already have one (to preserve validation messages from uploadService)
        setUploadError((prevError) => prevError || file.error?.message || "Nastala chyba pri nahrávaní ID karty.");
    }, [props]);

    const fileUploadCompleted = useCallback((file: Record<string, any>) => {
        file.uploadCompleted = true;
    }, []);

    const fileUploadProgressUpdate = useCallback((_file: Record<string, any>) => {
        // Progress update handler
    }, []);

    const uploadService = useCallback((input: any): Promise<Record<string, any>> => {
        return new Promise(function (resolve, reject) {
            const uploadIdCard = props.selectedSignatureMethod === SignatureMethod.Manual
                ? props.requestApi.uploadManualSignatureIdCard
                : props.requestApi.uploadIdCardWithValidation;

            uploadIdCard(input as any)
                .then(function (result) {
                    // Intermediate chunks: BE returns isValid=false + sessionId before the file is complete.
                    // Resolve so GenericChunkedUploader can send the next chunk (same as FileUploaderFactory).
                    if (!input.isUploadFinished) {
                        resolve({
                            sessionId: result.sessionId,
                            isValid: result.isValid,
                            frontFileId: result.frontFileId,
                            backFileId: result.backFileId,
                        });
                        return;
                    }

                    if (!result.isValid) {
                        const errorMessage = result.validationMessage || result.errorMessage || "Nepodarilo sa overiť ID kartu.";
                        setUploadError(errorMessage);
                        reject(new Error(errorMessage));
                        return;
                    }

                    if (result.errorMessage) {
                        setUploadError(result.errorMessage);
                        reject(new Error(result.errorMessage));
                        return;
                    }

                    // When upload is finished, update files with taxonomy IDs immediately
                    // This is critical for the getters to find the files and for the UI to update
                    if (result.frontFileId !== undefined && result.backFileId !== undefined) {
                        const requestWithFiles = getRequestWithFiles(props.request);

                        // Update front file with taxonomy ID
                        if (result.frontFileId && idCardFrontTaxonomyId.current) {
                            const frontFile = requestWithFiles.current.files.find(f => f.id === result.frontFileId);
                            if (frontFile) {
                                frontFile.taxonomyId = idCardFrontTaxonomyId.current;
                            }
                        }

                        // Update back file with taxonomy ID
                        if (result.backFileId && idCardBackTaxonomyId.current) {
                            const backFile = requestWithFiles.current.files.find(f => f.id === result.backFileId);
                            if (backFile) {
                                backFile.taxonomyId = idCardBackTaxonomyId.current;
                            }
                        }

                        // Also check the file that was just uploaded (input.fileToUpload.id)
                        // and assign taxonomy based on which file ID it matches
                        const uploadedFile = requestWithFiles.current.files.find(f => f.id === input.fileToUpload.id);
                        if (uploadedFile) {
                            if (result.frontFileId && uploadedFile.id === result.frontFileId && idCardFrontTaxonomyId.current) {
                                uploadedFile.taxonomyId = idCardFrontTaxonomyId.current;
                            } else if (result.backFileId && uploadedFile.id === result.backFileId && idCardBackTaxonomyId.current) {
                                uploadedFile.taxonomyId = idCardBackTaxonomyId.current;
                            }
                        }

                        // Trigger re-render to update UI immediately
                        rerenderForm();
                    }

                    // Success - return result with sessionId for chunked upload
                    resolve({
                        sessionId: result.sessionId,
                        isValid: result.isValid,
                        frontFileId: result.frontFileId,
                        backFileId: result.backFileId,
                    });
                })
                .catch(function (err) {
                    const errorMessage = err.response?.data?.validationMessage ||
                        err.response?.data?.errorMessage ||
                        err.message ||
                        "Nastala chyba pri nahrávaní ID karty.";
                    setUploadError(errorMessage);
                    reject(err);
                });
        });
    }, [props, idCardFrontTaxonomyId, idCardBackTaxonomyId]);

    const getUploader = useCallback(() => {
        // Create a wrapper that matches the expected signature
        const wrappedGenerateInput = (
            file: Record<string, any>,
            byteArray: Uint8Array,
            isEndOfFile: boolean
        ): GenerateUploadInput => {
            const input = generateUploadInput(file, byteArray, isEndOfFile);
            // Return in the format expected by GenericChunkedUploader
            // Note: We need to provide a dummy taxonomy for the type, but it won't be used
            return {
                requestId: input.requestId,
                requestTypeId: request.current.id,
                parameters: [],
                isUploadFinished: input.isUploadFinished ?? false,
                uploadSessionId: input.uploadSessionId,
                sendNotification: false,
                fileToUpload: {
                    ...input.fileToUpload,
                    id: input.fileToUpload.id,
                    taxonomy: {} as any, // Dummy taxonomy - not used by backend
                },
            } as GenerateUploadInput;
        };

        return CreateGenericChunkedUploader(
            wrappedGenerateInput,
            uploadService,
            uploadErrorHandler,
            3145728, // chunk size
            fileUploadProgressUpdate,
            fileUploadCompleted,
            1499000 // max pixel size
        );
    }, [generateUploadInput, uploadService, uploadErrorHandler, fileUploadProgressUpdate, fileUploadCompleted, request]);

    const handleUploadInternal = useCallback(async (filesToUpload: File[]) => {
        if (isUploading || filesToUpload.length === 0) return;

        // Check if both sides are still uploaded - if not, allow upload
        const frontFileId = getIdCardFrontFileId();
        const backFileId = getIdCardBackFileId();
        const bothSidesUploaded = frontFileId !== null && backFileId !== null;

        // If uploadSuccess is true but both sides are no longer uploaded, reset it
        if (uploadSuccess && !bothSidesUploaded) {
            setUploadSuccess(false);
        }

        // Block upload only if both sides are uploaded AND uploadSuccess is true
        if (uploadSuccess && bothSidesUploaded) return;

        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        let extendedFiles: ExtendedFile[] = [];

        try {
            // Convert File objects to ExtendedFile (no taxonomy assignment - backend handles it)
            extendedFiles = filesToUpload.map((file) => {
                const extendedFile = file as ExtendedFile;
                extendedFile.id = utilities.guid();
                extendedFile.extension = utilities.getFileExtension(file.name);
                extendedFile.uploadCompleted = false;
                extendedFile.requestId = request.current.requestId;
                extendedFile.requestTypeId = request.current.id;
                // No taxonomy assignment - backend will detect via AI
                return extendedFile;
            });

            // Add icons to files
            addIconsToFiles(extendedFiles, false, false);

            // Add files to request
            const requestWithFiles = getRequestWithFiles(props.request);
            extendedFiles.forEach(file => {
                requestWithFiles.current.files.push(file);
            });

            props.rerenderForm();
            props.uploadInProgressNotifierTask.debouncedPerform();

            // Upload files using the new endpoint
            const uploader = getUploader();
            await uploader.UploadFileFunction(extendedFiles);

            // Mark files as uploaded
            for (let fi = 0; fi < requestWithFiles.current.files.length; fi++) {
                let file = requestWithFiles.current.files[fi];
                if (extendedFiles.filter((ftr) => ftr.id === file.id).length > 0) {
                    file = { ...file };
                    requestWithFiles.current.files[fi] = file;
                }
            }

            // Save request after upload
            props.onlySaveRequest(undefined, false, () => {
                props.uploadInCompletedNotifierTask.debouncedPerform();
                setIsUploading(false);

                // Check if both sides are uploaded
                const frontFileId = getIdCardFrontFileId();
                const backFileId = getIdCardBackFileId();
                const bothSidesUploaded = frontFileId !== null && backFileId !== null;
                if (bothSidesUploaded) {
                    setUploadSuccess(true);
                    openSnackbar({ text: "Obe strany ID karty boli úspešne nahrané a overené." });
                } else {
                    // Show message based on which side is missing
                    if (frontFileId && !backFileId) {
                        openSnackbar({ text: "Predná strana bola úspešne nahraná. Prosím nahrajte zadnú stranu." });
                    } else if (backFileId && !frontFileId) {
                        openSnackbar({ text: "Zadná strana bola úspešne nahraná. Prosím nahrajte prednú stranu." });
                    }
                }
            });

        } catch (error: any) {
            setIsUploading(false);
            // Extract error message from backend response, or use the already set uploadError state, or fallback to generic message
            const errorMessage = error?.response?.data?.validationMessage ||
                error?.response?.data?.errorMessage ||
                error?.message ||
                uploadError ||
                "Nastala chyba pri nahrávaní ID kariet.";
            // Update uploadError state if we got a new message from the error
            if (errorMessage && errorMessage !== uploadError) {
                setUploadError(errorMessage);
            }
            openSnackbar({ text: errorMessage });

            // Clean up files from request on error
            try {
                const requestWithFiles = getRequestWithFiles(props.request);
                const extendedFileIds = extendedFiles.map((f: any) => f.id);
                requestWithFiles.current.files = requestWithFiles.current.files.filter(
                    (file: any) => !extendedFileIds.includes(file.id)
                );
                props.rerenderForm();
            } catch {
                // Ignore cleanup errors
            }
        }
    }, [isUploading, uploadSuccess, request, props, getUploader, openSnackbar, uploadError, getIdCardFrontFileId, getIdCardBackFileId]);

    // Get current file IDs and file objects using getters
    const frontFileId = getIdCardFrontFileId();
    const backFileId = getIdCardBackFileId();

    // Get full file objects from request
    const requestWithFiles = getRequestWithFiles(props.request);
    const frontFile = frontFileId ? requestWithFiles.current.files.find(f => f.id === frontFileId) : null;
    const backFile = backFileId ? requestWithFiles.current.files.find(f => f.id === backFileId) : null;

    // Reset uploadSuccess if a file is deleted (both sides no longer uploaded)
    useEffect(() => {
        const bothSidesUploaded = frontFileId !== null && backFileId !== null;
        if (uploadSuccess && !bothSidesUploaded) {
            setUploadSuccess(false);
        }
    }, [frontFileId, backFileId, uploadSuccess]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        // Limit to 2 files (front and back)
        const filesToStore = acceptedFiles.slice(0, 2);

        // Automatically trigger upload when files are dropped
        // Allow upload even if one side is already uploaded (user can replace or upload the other side)
        if (filesToStore.length > 0 && !isUploading) {
            // Use setTimeout to ensure state is updated before upload
            setTimeout(() => {
                handleUploadInternal(filesToStore);
            }, 0);
        }
    }, [isUploading, handleUploadInternal]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg'],
            'application/pdf': ['.pdf']
        },
        maxFiles: 2,
        disabled: isUploading
    });

    return (
        <Box
            sx={{
                ...signatureFlowContentGutterSx,
                ...signatureFlowBodyBottomGapSx,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                pt: 1,
            }}
        >
            {/* Content Area — section-style card (same shell as Section useCardChrome) */}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "7px",
                    }}
                >
                    <Box
                        className="section-header"
                        sx={{
                            padding: "24px",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            margin: 0,
                        }}
                    >
                        <Box
                            sx={{
                                ...getYCenterStyles(),
                                gap: theme.spacing(2),
                                justifyContent: "flex-start",
                            }}
                        >
                            <CardDocumentIcon width={32} height={32} color={theme.palette.primary.main} />
                            <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
                                Prosím nahrajte prednu a zadnu stranu občianského preukazu klienta
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        className="component-switcher-holder"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            width: "100%",
                            padding: "24px",
                        }}
                    >
                        {/* Drag and Drop Area - Only show when one or both sides are missing */}
                        {(!frontFileId || !backFileId) && (
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                }}
                            >
                                <Box
                                    {...getRootProps({
                                        role: "button",
                                        tabIndex: 0,
                                        "aria-label": "Nahrať prednú a zadnú stranu občianskeho preukazu",
                                    })}
                                    sx={{
                                        boxShadow: "0 0 0 2px transparent",
                                        border: "2px solid transparent",
                                        borderRadius: "8px",
                                        "&:focus-visible": {
                                            outline: "none",
                                            boxShadow: "0 0 0 2px rgba(0,0,0,0.1)",
                                        },
                                        width: "100%",
                                    }}
                                >
                                    <input
                                        {...getInputProps({
                                            tabIndex: -1,
                                            "aria-hidden": true,
                                        })}
                                    />
                                    <Box
                                        sx={{
                                            ...getCenterStyles("column"),
                                            width: "100%",
                                            flexShrink: 0,
                                            minHeight: 188,
                                            boxSizing: "border-box",
                                            padding: "20px",
                                            borderRadius: "7px",
                                            border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                                            backgroundColor: isDragActive
                                                ? alpha(theme.palette.primary.main, 0.06)
                                                : theme.palette.common.white,
                                            transition: "border-color 0.2s ease, background-color 0.2s ease",
                                            cursor: isUploading ? "not-allowed" : "pointer",
                                            opacity: isUploading ? 0.7 : 1,
                                            "&:hover": isUploading
                                                ? {}
                                                : {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: "100%",
                                                mb: 0.5,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 0.25,
                                                textAlign: "center",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                component="p"
                                                sx={{
                                                    fontWeight: 400,
                                                    fontSize: theme.typography.pxToRem(13),
                                                    lineHeight: "132%",
                                                    letterSpacing: 0,
                                                    textAlign: "center",
                                                    color: theme.palette.text.secondary,
                                                }}
                                            >
                                                Pripojiť súbory
                                            </Typography>
                                        </Box>

                                        <Box sx={{ ...getCenterStyles() }}>
                                            <CardDocumentIcon width={48} height={48} color={theme.palette.primary.main} />
                                        </Box>

                                        {uploadError && (
                                            <Box
                                                sx={{
                                                    marginTop: 2,
                                                    padding: 1,
                                                    width: "100%",
                                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography variant="body2" color="error">
                                                    {uploadError}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>

                                {/* Loading Overlay */}
                                {isUploading && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: alpha(theme.palette.background.paper, 0.9),
                                            borderRadius: "7px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 2,
                                            zIndex: 1,
                                        }}
                                    >
                                        <CircularProgress size={48} />
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: "medium",
                                            }}
                                        >
                                            Nahrávanie ID karty...
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                            }}
                                        >
                                            Prosím počkajte, kým sa dokončí nahrávanie a overenie
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* ID Card Sides Display */}
                        {(frontFile || backFile) && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {frontFile && (
                                    <FileTile
                                        {...props}
                                        file={frontFile}
                                        primaryLabel="Predná strana občianskeho preukazu"
                                        showDeleteButton={!!frontFile.uploadCompleted}
                                        showChangeTaxonomyButton={false}
                                        clickedStateStrategy="id"
                                    />
                                )}

                                {backFile && (
                                    <FileTile
                                        {...props}
                                        file={backFile}
                                        primaryLabel="Zadná strana občianskeho preukazu"
                                        showDeleteButton={!!backFile.uploadCompleted}
                                        showChangeTaxonomyButton={false}
                                        clickedStateStrategy="id"
                                    />
                                )}
                            </Box>
                        )}

                        {/* Status Messages */}
                        {!uploadError && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {frontFileId && !backFileId && (
                                    <Box sx={{ padding: 1, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
                                        <Typography variant="body2" color="info.main">
                                            Predná strana bola úspešne nahraná. Prosím nahrajte zadnú stranu.
                                        </Typography>
                                    </Box>
                                )}
                                {backFileId && !frontFileId && (
                                    <Box sx={{ padding: 1, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
                                        <Typography variant="body2" color="info.main">
                                            Zadná strana bola úspešne nahraná. Prosím nahrajte prednú stranu.
                                        </Typography>
                                    </Box>
                                )}
                                {frontFileId && backFileId && (
                                    <Box sx={{ padding: 1, backgroundColor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                                        <Typography variant="body2" color="success.main">
                                            {props.selectedSignatureMethod === SignatureMethod.Manual
                                                ? "Obe strany boli úspešne nahrané. Teraz môžete podpísať žiadosť."
                                                : "Obe strany boli úspešne nahrané. Teraz môžete vytvoriť magic link."}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

        </Box>
    );
};

